import React, { ReactElement } from 'react';
import { useEffect } from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { loadMessages } from '@progress/kendo-react-intl';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import esMessages from '../../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import {
  AssetStatePropertiesCountsByDateRange,
  AssetStatePropertiesByCategoryCount,
} from '../../../Interfaces/Devices/Trends/AssetStatePropertiesCountsByDateRange';
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  SeriesClickEvent,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
} from '@progress/kendo-react-charts';
import {
  isNullOrEmptyString,
  convertToLocaleDateString,
  convertToLocaleTimeString,
} from '../../../utils/utils';
import { GetString } from '../../StringResourceManager';
import { isMatch } from '../../../utils/textMappings';
import { Dialog } from '@progress/kendo-react-dialogs';
import DeviceIssueTrendGrid from './DeviceIssueTrendsGrid';
import { Label } from '@progress/kendo-react-labels';
import { Loader } from '@progress/kendo-react-indicators';
import { ApiEnums } from '../../../Interfaces/ApiEnums';

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    lineChartHeaderContainer: {
      margin: 20,
    },
    lineChartHeader: {
      fontSize: 20,
      textDecorationLine: 'underline',
      marginBottom: 10,
      fontWeight: 'bold',
    },
    queryLabel: {
      display: 'flex',
      flexDirection: 'row',
      fontSize: 18,
    },
    queryInput: {
      fontWeight: 'bold',
      fontSize: 18,
      paddingLeft: 5,
    },
  })
);

const DeviceIssueTrendsLineChart: React.FC = (): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const assetStatePropertiesCountsByDateRange: AssetStatePropertiesCountsByDateRange =
    useSelector(
      (state: ApplicationState) =>
        state.assetStatusesReducer.assetStatePropertiesCountsByDateRange
    );

  const isFetching: boolean = useSelector(
    (state: ApplicationState) =>
      state.assetStatusesReducer.isFetchingAssetStatePropertiesCountsByDateRange
  );

  const [dateList, setDateList] = React.useState<string[]>([]);

  const [countList, setCountList] = React.useState<number[]>([]);

  const [categoryListByDate, setCategoryListByDate] = React.useState<
    number[][]
  >([]);

  const [categoryList, setCategoryList] = React.useState<string[]>([]);

  const [selectedDate, setSelectedDate] = React.useState<string>('');

  const [selectedCategory, setSelectedCategory] = React.useState<string>('');

  const [selectedValue, setSelectedValue] = React.useState<string>('');

  const [deviceIssueTrendGridTitle, setDeviceIssueTrendGridTitle] =
    React.useState<string>(GetString('DEVICE_ISSUE_HISTORY'));

  const [deviceIssueTrendGridVisible, setDeviceIssueTrendGridVisible] =
    React.useState<boolean>(false);

  const ALL = GetString('ALL');

  const [selectedSeriesName, setSelectedSeriesName] =
    React.useState<string>('');

  const [highestIssueCount, setHighestIssueCount] = React.useState<number>(0);

  useEffect(() => {
    var dates: string[] = [];
    var counts: number[] = [];
    let highestIssueCount: number = 0;

    setCategoryList([]);
    setCategoryListByDate([]);

    assetStatePropertiesCountsByDateRange.Counts.pop();
    for (
      let i = 0;
      i < assetStatePropertiesCountsByDateRange.Counts.length;
      i++
    ) {
      dates.push(
        convertToLocaleDateString(
          assetStatePropertiesCountsByDateRange.Counts[i].Date
        )
      );
      let count = assetStatePropertiesCountsByDateRange.Counts[i].Count;
      counts.push(count);

      if (count > highestIssueCount) highestIssueCount = count;

      if (
        isMatch('ALL', assetStatePropertiesCountsByDateRange.AssetInfo.Category)
      )
        for (
          let l = 0;
          l <
          assetStatePropertiesCountsByDateRange.Counts[i]
            .AssetStatePropertiesByCategoryListCount.length;
          l++
        ) {
          var category =
            assetStatePropertiesCountsByDateRange.Counts[i]
              .AssetStatePropertiesByCategoryListCount[l].Category;
          if (!categoryList.includes(category)) {
            categoryList.push(category);
          }
        }

      setCategoryList(categoryList);
    }

    if (
      isMatch('ALL', assetStatePropertiesCountsByDateRange.AssetInfo.Category)
    )
      setCategoryCountArrayTableByDate(categoryList, dates);
    else {
      setHighestIssueCount(highestIssueCount);
    }

    setDateList(dates);
    setCountList(counts);

    setSelectedCategory(processCategoryTitle());
    setSelectedValue(processIssueTitle());
  }, [assetStatePropertiesCountsByDateRange]);

  const setCategoryCountArrayTableByDate = (
    categoryList: string[],
    dateList: string[]
  ) => {
    let rows: number = categoryList.length;
    let columns: number = dateList.length;
    let arrayTable: number[][] = [];
    let highestIssueCount: number = 0;

    for (let i = 0; i < rows; i++) {
      arrayTable[i] = [];
      for (let j = 0; j < columns; j++) {
        arrayTable[i][j] = 0;
      }
    }

    for (
      let i = 0;
      i < assetStatePropertiesCountsByDateRange.Counts.length;
      i++
    ) {
      for (
        let l = 0;
        l <
        assetStatePropertiesCountsByDateRange.Counts[i]
          .AssetStatePropertiesByCategoryListCount.length;
        l++
      ) {
        let aspCategoryCount: AssetStatePropertiesByCategoryCount =
          assetStatePropertiesCountsByDateRange.Counts[i]
            .AssetStatePropertiesByCategoryListCount[l];

        var categoryIndex = categoryList.indexOf(aspCategoryCount.Category);

        var dateIndex = dateList.indexOf(
          convertToLocaleDateString(
            assetStatePropertiesCountsByDateRange.Counts[i].Date
          )
        );

        arrayTable[categoryIndex][dateIndex] = aspCategoryCount.Count;

        if (aspCategoryCount.Count > highestIssueCount)
          highestIssueCount = aspCategoryCount.Count;
      }
    }

    setHighestIssueCount(highestIssueCount);
    setCategoryListByDate(arrayTable);
  };

  const processCategoryTitle = () => {
    if (assetStatePropertiesCountsByDateRange.AssetInfo.Category == null)
      return '';

    var category = assetStatePropertiesCountsByDateRange.AssetInfo.Category;
    return isMatch('ALL', category) ? ALL : category;
  };

  const processIssueTitle = () => {
    if (assetStatePropertiesCountsByDateRange.AssetInfo.Value == null)
      return '';

    var value = assetStatePropertiesCountsByDateRange.AssetInfo.Value;
    return isNullOrEmptyString(value) ? ALL : value;
  };

  const onChartSeriesClick = (event: SeriesClickEvent) => {
    setSelectedSeriesName(event.series.name);
    setSelectedDate(event.category);
    toggleDeviceIssueGridDialog();
  };

  const toggleDeviceIssueGridDialog = () => {
    setDeviceIssueTrendGridVisible(!deviceIssueTrendGridVisible);
  };

  const formatDate = (date: Date) => {
    return (
      convertToLocaleDateString(date) + ' ' + convertToLocaleTimeString(date)
    );
  };

  function isSingleDateRangeOption(from: Date, to: Date): boolean {
    const fromDate = new Date(from);
    const toDate = new Date(to);

    const oneDayMs = 24 * 60 * 60 * 1000;
    const diff = toDate.getTime() - fromDate.getTime();
    return diff === oneDayMs;
  }

  function isBarType(): boolean {
    let isBarType = false;
    var data = assetStatePropertiesCountsByDateRange;
    if (
      data.DateRangeOption == ApiEnums.DateRangeOptions.Today ||
      data.DateRangeOption == ApiEnums.DateRangeOptions.Yesterday ||
      isSingleDateRangeOption(data.StartDate, data.EndDate)
    )
      isBarType = true;

    return isBarType;
  }

  const ChartContainer = () => (
    <div>
      {isFetching ? (
        <div
          style={{
            textAlign: 'center',
          }}
        >
          <Loader size="large" type={'pulsing'} />
        </div>
      ) : (
        <Chart onSeriesClick={onChartSeriesClick}>
          <ChartTooltip shared={true} />
          <ChartCategoryAxis>
            <ChartCategoryAxisItem
              title={{
                text: GetString('DATES'),
              }}
              labels={{
                rotation: 'auto',
              }}
              categories={dateList}
            />
          </ChartCategoryAxis>
          <ChartValueAxis>
            <ChartValueAxisItem
              title={{ text: GetString('COUNTS') }}
              max={highestIssueCount <= 5 ? 5 : undefined}
              majorUnit={highestIssueCount <= 5 ? 1 : undefined}
              min={0}
            />
          </ChartValueAxis>
          <ChartSeries>
            {isMatch(
              'ALL',
              assetStatePropertiesCountsByDateRange.AssetInfo.Category
            ) ? (
              categoryListByDate.map((item, idx) => (
                <ChartSeriesItem
                  name={categoryList[idx]}
                  key={idx}
                  type={isBarType() ? 'bar' : 'line'}
                  tooltip={{
                    visible: true,
                  }}
                  data={categoryListByDate[idx]}
                  markers={{ visible: false }}
                />
              ))
            ) : (
              <ChartSeriesItem
                type={isBarType() ? 'bar' : 'line'}
                data={countList}
                markers={{ visible: false }}
              />
            )}
          </ChartSeries>
        </Chart>
      )}
    </div>
  );

  return (
    <React.Fragment>
      <div className={classes.lineChartHeaderContainer}>
        <Label className={classes.lineChartHeader}>
          {GetString('DEVICE_ISSUE_HISTORY')}
        </Label>
        <div className={classes.queryLabel}>
          <Label>{GetString('CATEGORY')}</Label>
          <Label className={classes.queryInput}>{selectedCategory}</Label>
        </div>
        <div className={classes.queryLabel}>
          <Label>{GetString('ISSUE')}</Label>
          <Label className={classes.queryInput}>{selectedValue}</Label>
        </div>
        <div className={classes.queryLabel}>
          <Label>{GetString('START_DATE_TIME')}</Label>
          <Label className={classes.queryInput}>
            {formatDate(assetStatePropertiesCountsByDateRange.StartDate)}
          </Label>
        </div>
        <div className={classes.queryLabel}>
          <Label>{GetString('END_DATE_TIME')}</Label>
          <Label className={classes.queryInput}>
            {formatDate(assetStatePropertiesCountsByDateRange.EndDate)}
          </Label>
        </div>
      </div>
      <ChartContainer />
      {deviceIssueTrendGridVisible && (
        <Dialog
          title={deviceIssueTrendGridTitle}
          onClose={toggleDeviceIssueGridDialog}
          width="90vw"
          height="95vh"
        >
          <DeviceIssueTrendGrid
            date={selectedDate}
            seriesName={selectedSeriesName}
          />
        </Dialog>
      )}
    </React.Fragment>
  );
};

export default DeviceIssueTrendsLineChart;
